/*
 * Created on Wed Oct 11 2023 4:56:55 PM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023 
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */
import { useState, useEffect } from 'react'; 
import { useDispatch, useSelector } from 'react-redux';
import { getAllOrdersByAccountId } from '../../../../../../api/action/order';
import { getOrderPaymentReference } from '../utils/orderUtils';

const ALLOWED_STATUSES = ['PAID', 'REFUND_PENDING', 'CANCELLED', 'REFUNDED', 'PARTIALLY_REFUNDED', 'PAYMENT_PENDING', 'PAYMENT_PROCESSING'];

const attachParentPaymentReferences = (orders = []) => {
    if (!Array.isArray(orders) || orders.length === 0) {
        return [];
    }

    const parentReferenceMap = new Map();

    orders.forEach((order) => {
        const paymentReference = getOrderPaymentReference(order);
        if (!paymentReference) return;

        const orderId = order?.primaryId ? String(order.primaryId) : '';
        const orderNumber = order?.number ? String(order.number) : '';

        if (orderId) {
            parentReferenceMap.set(orderId, paymentReference);
        }
        if (orderNumber) {
            parentReferenceMap.set(orderNumber, paymentReference);
        }
    });

    const enrichOrder = (order) => {
        if (!order) return order;

        const parentOrderKey = order?.parentOrderNumber ? String(order.parentOrderNumber) : '';
        const directReference = getOrderPaymentReference(order);
        const inheritedParentReference = parentOrderKey ? (parentReferenceMap.get(parentOrderKey) || '') : '';

        return {
            ...order,
            parentPaymentReference: directReference || inheritedParentReference || order?.parentPaymentReference || '',
            childOrders: Array.isArray(order?.childOrders)
                ? order.childOrders.map((childOrder) => {
                    const childReference = getOrderPaymentReference(childOrder);
                    return {
                        ...childOrder,
                        parentPaymentReference: childReference || directReference || inheritedParentReference || ''
                    };
                })
                : order?.childOrders
        };
    };

    return orders.map(enrichOrder);
};

const buildDisplayOrders = (orders = []) => {
    if (!Array.isArray(orders) || orders.length === 0) {
        return [];
    }

    const displayOrders = [];
    const seenOrderIds = new Set();

    const pushDisplayOrder = (order, parentOrder = null) => {
        if (!order?.primaryId) return;

        const orderId = String(order.primaryId);
        if (seenOrderIds.has(orderId)) {
            return;
        }

        const parentOrderId = parentOrder?.primaryId || order?.parentOrderId || order?.parentOrderNumber || null;
        const parentReference = parentOrder ? getOrderPaymentReference(parentOrder) : '';
        const directReference = getOrderPaymentReference(order);

        displayOrders.push({
            ...order,
            isGroupedOrder: false,
            childOrders: undefined,
            parentOrder: parentOrder || order?.parentOrder || null,
            parentOrderId,
            parentOrderNumber: order?.parentOrderNumber || parentOrderId,
            parentPaymentReference: directReference || parentReference || order?.parentPaymentReference || '',
            isRecurringChild: Boolean(parentOrderId)
        });

        seenOrderIds.add(orderId);
    };

    orders.forEach((order) => {
        if (Array.isArray(order?.childOrders) && order.childOrders.length > 0) {
            order.childOrders.forEach((childOrder) => pushDisplayOrder(childOrder, order));
            return;
        }

        pushDisplayOrder(order);
    });

    return displayOrders;
};
 
const useOrderData = () => {
    const dispatch = useDispatch();
    const companyDetails = useSelector((state) => state?.companyReducer?.company);
    const userDetail = useSelector((state) => state?.auth?.data) || {};
    
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                setLoading(true);
                setError(null);
                const accountId = userDetail?.account?.primaryId || '';

                if (accountId) {
                    const payload = {
                        accountId: accountId,
                        company: { primaryId: companyDetails?.primaryId },
                        // currentStatus: 'PAID'
                    };

                    const response = await dispatch(getAllOrdersByAccountId(payload));

                    let ordersData = [];
                    if (response && Array.isArray(response)) {
                        ordersData = response;
                    } else if (response?.payload && Array.isArray(response.payload)) {
                        ordersData = response.payload;
                    } else if (response?.data && Array.isArray(response.data)) {
                        ordersData = response.data;
                    } else if (response?.orders && Array.isArray(response.orders)) {
                        ordersData = response.orders;
                    }

                    const filteredByStatus = ordersData.filter(order => {
                        const status = order?.currentStatus || order?.status;
                        return ALLOWED_STATUSES.includes(status);
                    });

                    setOrders(buildDisplayOrders(attachParentPaymentReferences(filteredByStatus)));
                } else {
                    setOrders([]);
                }
            } catch (err) {
                setError(err.message || 'Failed to fetch orders');
                setOrders([]);
            } finally {
                setLoading(false);
            }
        };

        fetchOrders();
    }, [dispatch, userDetail?.account?.primaryId, companyDetails?.primaryId]);

    return { orders, setOrders, loading, error };
};

export default useOrderData;
